import React, { useState, useEffect } from "react";
import { Dialog, TextField, MenuItem, Box, Alert, CircularProgress } from "@mui/material";
import axiosInstance from "services/api/axiosInstance";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { modalStyles } from "components/Modal/modalStyles";

const ROLES = [
  { value: "super_admin", label: "Super Admin", description: "Full read/write across all tenants and modules" },
  { value: "tenant_admin", label: "Tenant Admin", description: "Full control over assigned tenants" },
  { value: "billing_manager", label: "Billing Manager", description: "Read/write to subscription and cost modules" },
  { value: "auditor", label: "Auditor", description: "Read-only across all modules including audit logs" },
  { value: "readonly_viewer", label: "Read-Only Viewer", description: "Dashboard-only access, no write permissions" },
];

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  role: "",
  assignedTenants: "",
};

const ROLE_LABEL_TO_VALUE = {
  "Super Admin": "super_admin",
  "Tenant Admin": "tenant_admin",
  "Billing Manager": "billing_manager",
  "Auditor": "auditor",
  "Read-Only Viewer": "readonly_viewer",
};

/**
 * AddUserModal — handles both Add and Edit modes
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onSubmit: (data) => void
 *   - user: object | null — if provided, modal opens in Edit mode
 */
function AddUserModal({ open, onClose, onSubmit, user }) {
  const isEditMode = !!user;

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Pre-fill form when editing
  useEffect(() => {
    if (user && open) {
      const nameParts = (user.name || "").split(" ");
      setForm({
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: user.email || "",
        password: "",
        role: ROLE_LABEL_TO_VALUE[user.role] || user.role || "",
        assignedTenants: (user.tenants || []).join(", "),
      });
    } else if (!user && open) {
      setForm(INITIAL_FORM);
    }
    setErrors({});
    setApiError(null);
    setSuccess(false);
  }, [user, open]);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
    if (apiError) setApiError(null);
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "First name is required";
    if (!form.lastName.trim()) errs.lastName = "Last name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email format";
    if (!isEditMode) {
      if (!form.password.trim()) errs.password = "Password is required";
      else if (form.password.length < 6) errs.password = "Password must be at least 6 characters";
    }
    if (!form.role) errs.role = "Role is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setApiError(null);
    setSuccess(false);

    try {
      const tenants = form.assignedTenants
        ? form.assignedTenants.split(",").map((t) => t.trim()).filter(Boolean)
        : [];

      let response;

      if (isEditMode) {
        // Update user
        response = await axiosInstance.put(`${process.env.REACT_APP_CONSOLE_API}/console/v1/auth/updateUser`, {
          userId: user._id || user.userId,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          tenants,
          role: form.role,
        });
      } else {
        // Create user
        response = await axiosInstance.post(`${process.env.REACT_APP_CONSOLE_API}/console/v1/auth/createUser`, {
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          tenants,
          role: form.role,
        });
      }

      setSuccess(true);
      if (onSubmit) onSubmit(response.data);

      setTimeout(() => {
        setForm(INITIAL_FORM);
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        `Failed to ${isEditMode ? "update" : "create"} user. Please try again.`;
      setApiError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setApiError(null);
    setSuccess(false);
    onClose();
  };

  const selectedRole = ROLES.find((r) => r.value === form.role);

  return (
    <Dialog open={open} onClose={handleCancel} PaperProps={{ sx: modalStyles.paper }}>
      {/* Header */}
      <MDBox sx={modalStyles.header}>
        <MDBox>
          <MDTypography sx={modalStyles.headerTitle}>
            {isEditMode ? "Edit User" : "Add New User"}
          </MDTypography>
          <MDTypography sx={modalStyles.headerSubtitle}>
            {isEditMode ? "Update user details and role assignment" : "Create a new platform user with role-based access"}
          </MDTypography>
        </MDBox>
        <Box component="button" sx={modalStyles.closeButton} onClick={handleCancel}>
          ✕
        </Box>
      </MDBox>

      {/* Body */}
      <MDBox sx={modalStyles.body}>
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError(null)}>
            {apiError}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            User {isEditMode ? "updated" : "created"} successfully!
          </Alert>
        )}

        {/* Name fields */}
        <MDBox display="flex" gap={2} sx={modalStyles.fieldGroup}>
          <MDBox flex={1}>
            <MDTypography component="label" sx={modalStyles.fieldLabel}>First Name *</MDTypography>
            <TextField
              fullWidth
              size="small"
              placeholder="John"
              value={form.firstName}
              onChange={handleChange("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName}
              disabled={loading}
              sx={modalStyles.input}
            />
          </MDBox>
          <MDBox flex={1}>
            <MDTypography component="label" sx={modalStyles.fieldLabel}>Last Name *</MDTypography>
            <TextField
              fullWidth
              size="small"
              placeholder="Doe"
              value={form.lastName}
              onChange={handleChange("lastName")}
              error={!!errors.lastName}
              helperText={errors.lastName}
              disabled={loading}
              sx={modalStyles.input}
            />
          </MDBox>
        </MDBox>

        {/* Email */}
        <MDBox sx={modalStyles.fieldGroup}>
          <MDTypography component="label" sx={modalStyles.fieldLabel}>Email Address *</MDTypography>
          <TextField
            fullWidth
            size="small"
            placeholder="john.doe@company.io"
            value={form.email}
            onChange={handleChange("email")}
            error={!!errors.email}
            helperText={isEditMode ? "Email cannot be changed" : errors.email}
            disabled={loading || isEditMode}
            sx={modalStyles.input}
          />
        </MDBox>

        {/* Password — only shown in Add mode */}
        {!isEditMode && (
          <MDBox sx={modalStyles.fieldGroup}>
            <MDTypography component="label" sx={modalStyles.fieldLabel}>Password *</MDTypography>
            <TextField
              fullWidth
              size="small"
              type="password"
              placeholder="Minimum 6 characters"
              value={form.password}
              onChange={handleChange("password")}
              error={!!errors.password}
              helperText={errors.password}
              disabled={loading}
              sx={modalStyles.input}
            />
          </MDBox>
        )}

        {/* Role */}
        <MDBox sx={modalStyles.fieldGroup}>
          <MDTypography component="label" sx={modalStyles.fieldLabel}>Role *</MDTypography>
          <TextField
            fullWidth
            select
            size="small"
            value={form.role}
            onChange={handleChange("role")}
            error={!!errors.role}
            helperText={errors.role}
            disabled={loading}
            sx={modalStyles.select}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              <span style={{ color: "#BDC3C7" }}>Select a role...</span>
            </MenuItem>
            {ROLES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          {selectedRole && (
            <MDTypography sx={modalStyles.helperText}>
              {selectedRole.description}
            </MDTypography>
          )}
        </MDBox>

        {/* Assigned Tenants (optional for tenant_admin) */}
        {form.role === "tenant_admin" && (
          <MDBox sx={modalStyles.fieldGroup}>
            <MDTypography component="label" sx={modalStyles.fieldLabel}>Assigned Tenants</MDTypography>
            <TextField
              fullWidth
              size="small"
              placeholder="Comma-separated tenant IDs (e.g., T-001, T-002)"
              value={form.assignedTenants}
              onChange={handleChange("assignedTenants")}
              disabled={loading}
              sx={modalStyles.input}
            />
            <MDTypography sx={modalStyles.helperText}>
              Leave empty to assign later
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      {/* Footer */}
      <MDBox sx={modalStyles.footer}>
        <Box component="button" sx={modalStyles.secondaryButton} onClick={handleCancel} disabled={loading}>
          Cancel
        </Box>
        <Box
          component="button"
          sx={{
            ...modalStyles.primaryButton,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : isEditMode ? "Update User" : "Add User"}
        </Box>
      </MDBox>
    </Dialog>
  );
}

export default AddUserModal;
